export interface DashboardLike {
  id: string;
  slug: string;
  title: string;
  url: string;
  category: string;
  school_year: string;
  archived: boolean;
  type?: "looker" | "sheets" | "spark";
  sparkDashboardId?: string | null;
}

/**
 * Width of the box we hand Looker's embed viewer, which scales the report canvas
 * to fill it. Not a page-layout width — it is the typical authored canvas width,
 * measured across three live reports (1200 / ~1300 / 1400). 1300 is within 8% of
 * all three and still fits a 1366-wide screen. See SIM-3479.
 */
export const LOOKER_CANVAS_WIDTH_PX = 1300;

/**
 * True for a Looker Studio report URL, on either host and with or without the
 * `/u/N/` account-selector prefix that `buildEmbedUrl` strips. `PortalDashboard.type`
 * is not usable for this — it reads `looker` for every Sheet and Slide too (SIM-3509).
 */
export function isLookerReport(url: string): boolean {
  return /(?:datastudio|lookerstudio)\.google\.com\/(?:u\/\d+\/)?(?:embed\/)?reporting\//.test(url);
}

/** Convert a Looker Studio reporting URL to its embed equivalent. */
export function buildEmbedUrl(url: string): string {
  // Strip Google account selector prefix (/u/0/, /u/1/, etc.) — these cause
  // frame-ancestors 'none' CSP errors because they redirect to the signed-in UI.
  const cleaned = url.replace(/\/u\/\d+\//, "/");
  return cleaned.replace("/reporting/", "/embed/reporting/");
}

/** Find a dashboard by slug, returning null if not found. */
export function findDashboardBySlug(
  dashboards: DashboardLike[],
  slug: string | undefined,
): DashboardLike | null {
  if (!slug) return null;
  return dashboards.find((d) => d.slug === slug) ?? null;
}

/** Get all dashboards except the one with the given id (for the switcher dropdown). */
export function getOtherDashboards(
  dashboards: DashboardLike[],
  currentId: string,
): DashboardLike[] {
  return dashboards.filter((d) => d.id !== currentId);
}
